import { Icons, Progress } from '@agro-club/agroclub-shared'
import { useAProgress } from 'hooks/useAProgress'
import { useNotification } from 'hooks/useNotification'
import generateBidLink from 'modules/domain/bid/generateBidLink'
import { refetchFunc } from 'modules/domain/common/hooks'
import { ProfileType, User } from 'modules/domain/user/types'
import { UserAddress } from 'modules/domain/userAddress/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AddressExtraInfo } from 'views/components/AddressTitle/AddressTitle'
import { BorderedItem } from 'views/components/BorderedItem/BorderedItem'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { SharedModalContent, SharedModalTitle } from 'views/components/SharedModal'
import ASharedModal from 'views/components/SharedModal/ASharedModal'
import { WarehouseParamsEditModal } from 'views/components/WarehouseModal/WarehouseParamsEditModal'
import { AddressItemMenu } from './AddressItemMenu'
import { EditAddressModal } from './EditAddressModal'
import { BlockText, ItemText, TypeAddress } from './styled'
import { AddressMenuData } from './types'
import { RequestError } from 'modules/errors'

type Props = {
  addressesRefetch: refetchFunc
  showRemoveIcon?: boolean
  address: UserAddress
  user: User
}

export const AddressItem: FC<Props> = ({ addressesRefetch, showRemoveIcon, address, user }) => {
  const { t } = useTranslation('address')
  const notify = useNotification()
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [showEditParamsModal, setShowEditParamsModal] = useState<boolean>(false)
  const [removeProgress, removeAddress] = useAProgress(
    (addressId: string) => apiClient.delete(endpoints.userAddress(user.id, addressId)),
    {
      eventName: 'removeAddress',
      onSuccess: () => {
        addressesRefetch(true)
        notify(Progress.SUCCESS, { title: t('delete_success') })
      },
      onError: (e) => {
        const { errors } = e as RequestError
        notify(Progress.ERROR, { title: errors.message })
      },
    },
  )

  const dropdownData: AddressMenuData[] = [
    {
      label: t('showPrices'),
      icon: <Icons.IconCalculator />,
      link: {
        to: generateBidLink(
          user.profile?.profile_type === ProfileType.seller || user.profile?.profile_type === ProfileType.manager,
          address,
        ),
        eventName: 'calculate',
      },
      isShown: true,
    },
    {
      label: t('copyAddress'),
      icon: <Icons.IconCopy />,
      isShown: true,
      onClick: () => {
        navigator.clipboard.writeText(address.address)
      },
    },
    {
      label: t('editAddressParams'),
      icon: <Icons.IconGear />,
      isShown: true,
      onClick: () => setShowEditParamsModal(true),
    },
    {
      label: t('editAddress'),
      icon: <Icons.IconEdit />,
      isShown: true,
      onClick: () => setShowEditModal(true),
    },
    {
      label: t('deleteAddress'),
      icon: <Icons.IconDelete />,
      isShown: showRemoveIcon,
      onClick: () => setShowDeleteModal(true),
    },
  ].filter((item) => item.isShown)

  return (
    <>
      <BorderedItem>
        <BlockText>
          <AddressExtraInfo address={address} />
          <ItemText>{address.address}</ItemText>
          <TypeAddress>{address.is_main ? t('main') : t('additional')}</TypeAddress>
        </BlockText>
        <AddressItemMenu data={dropdownData} />
      </BorderedItem>

      {showDeleteModal && (
        <ASharedModal id="RemoveAddressModal" size="small" onClose={() => setShowDeleteModal(false)}>
          <SharedModalTitle>{t('addressDeleting')}</SharedModalTitle>
          <SharedModalContent>{t('areYouSureToDelete')}</SharedModalContent>
          <ADefaultModalFooter
            onClose={() => setShowDeleteModal(false)}
            progress={removeProgress}
            onConfirm={() => removeAddress(address.id)}
          />
        </ASharedModal>
      )}
      {showEditModal && (
        <EditAddressModal
          setShowModal={setShowEditModal}
          userId={user.id}
          address={address}
          addressesRefetch={addressesRefetch}
        />
      )}
      {showEditParamsModal && (
        <WarehouseParamsEditModal
          onClose={() => setShowEditParamsModal(false)}
          onSuccess={addressesRefetch}
          userAddress={address}
          userId={user.id}
          userType={user.profile?.profile_type}
        />
      )}
    </>
  )
}
